import { useCallback, useEffect, useMemo, useState } from "react";
// Material UI
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import { alpha, useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
// Components
import MDBox from "components/MDBox";
// Services
import { getMelhorEnvioSaldo } from "services/lojaFrete";

const SALDO_MINIMO_RECARGA = 50;

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

/** Extrai número de string BR/US (ex.: "1.624,90" ou "1624.9"). */
function parseMoneyish(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  let normalized = s;
  if (hasComma && hasDot) {
    // Assume milhar com ponto e decimal com vírgula (pt-BR)
    normalized = s.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = s.replace(",", ".");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Corpo esperado do GET /api/v2/me/balance (doc Melhor Envio):
 * `{ "balance": number, "reserved": number, "debts": number }`
 */
function pickNumericBalance(payload: unknown): number | null {
  if (payload == null) return null;
  if (typeof payload === "number" && Number.isFinite(payload)) {
    return payload;
  }
  if (typeof payload === "string") {
    return parseMoneyish(payload);
  }
  if (typeof payload !== "object") return null;
  const o = payload as Record<string, unknown>;
  const nested =
    typeof o.data === "object" && o.data !== null
      ? (o.data as Record<string, unknown>)
      : null;
  const candidates = [
    o.balance,
    o.saldo,
    o.amount,
    o.value,
    nested?.balance,
  ];
  for (const c of candidates) {
    if (typeof c === "number" && Number.isFinite(c)) return c;
    if (typeof c === "string") {
      const n = parseMoneyish(c);
      if (n != null) return n;
    }
  }
  return null;
}

function readAxiosErrorMessage(err: unknown): string {
  const e = err as {
    response?: {
      data?: { message?: string; error?: string; details?: unknown };
    };
    message?: string;
  };
  const d = e?.response?.data;
  const fromDetails =
    typeof d?.details === "string"
      ? d.details
      : d?.details &&
          typeof d.details === "object" &&
          "message" in (d.details as object)
        ? String((d.details as { message?: string }).message)
        : "";
  return (
    d?.message ||
    d?.error ||
    fromDetails ||
    e?.message ||
    "Não foi possível carregar o saldo."
  );
}

export type MelhorEnvioSaldoCardProps = {
  /** Se falso, não chama a API e exibe aviso (ex.: integração desligada). */
  enabled?: boolean;
  /** Ambiente da configuração salva (apenas rótulo visual). */
  sandbox?: boolean;
  sx?: SxProps<Theme>;
  compact?: boolean;
};

/**
 * Cartão com saldo da carteira no Melhor Envio (`GET /loja/frete/saldo`).
 * Reutilizável em qualquer tela do dashboard.
 */
export default function MelhorEnvioSaldoCard({
  enabled = true,
  sandbox,
  sx,
  compact = false,
}: MelhorEnvioSaldoCardProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const [saldo, setSaldo] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accent =
    mode === "dark"
      ? alpha("#34d399", 0.95)
      : (theme.palette.success?.main ?? "#16a34a");
  const borderSoft =
    mode === "dark" ? alpha("#34d399", 0.35) : alpha("#16a34a", 0.22);
  const wash = mode === "dark" ? alpha("#34d399", 0.1) : alpha("#16a34a", 0.07);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      // O interceptor do `api` pode *resolver* a Promise com `error.response` (4xx/5xx),
      // então não basta desestruturar `data`: é preciso checar `status`.
      const res = await getMelhorEnvioSaldo();
      if (res.status < 200 || res.status >= 300) {
        setSaldo(null);
        setError(readAxiosErrorMessage({ response: res }));
        return;
      }
      const n = pickNumericBalance(res.data);
      setSaldo(n);
      if (n == null) {
        const body = res.data as Record<string, unknown> | null;
        const apiMsg =
          body && typeof body.message === "string" ? body.message : "";
        setError(
          apiMsg ||
            "Não foi possível ler o saldo. Confira credenciais e ambiente (sandbox/produção) no Melhor Envio.",
        );
      }
    } catch (e) {
      setSaldo(null);
      setError(readAxiosErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setSaldo(null);
      setError(null);
      setLoading(false);
      return;
    }
    void load();
  }, [enabled, load]);

  const valueSxColor = useMemo(() => {
    if (!enabled || error) return "text.secondary";
    if (saldo != null && saldo < SALDO_MINIMO_RECARGA) return "warning.main";
    return accent;
  }, [accent, enabled, error, saldo]);

  return (
    <MDBox sx={sx}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          background: `linear-gradient(135deg, ${wash} 0%, transparent 55%)`,
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <MDBox
          p={compact ? 2 : 2.5}
          display="flex"
          alignItems={compact ? "center" : "flex-start"}
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Stack
            direction={compact ? "row" : "column"}
            spacing={compact ? 2 : 1}
            alignItems={compact ? "center" : "flex-start"}
            flex={1}
            minWidth={0}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              minWidth={0}
              flexWrap="wrap"
              useFlexGap
            >
              <AccountBalanceWalletOutlinedIcon
                fontSize={compact ? "medium" : "large"}
                sx={{
                  color: accent,
                  flexShrink: 0,
                }}
              />
              <div>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  letterSpacing={0.3}
                  display="block"
                >
                  Saldo na carteira Melhor Envio
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ opacity: 0.85 }}
                >
                  Valor disponível para pagamento de fretes (checkout no ME)
                </Typography>
              </div>
              {sandbox !== undefined ? (
                <Chip
                  size="small"
                  label={sandbox ? "Sandbox" : "Produção"}
                  color={sandbox ? "default" : "success"}
                  variant="outlined"
                  sx={{ height: 22, fontWeight: 600 }}
                />
              ) : null}
            </Stack>

            {!compact ? (
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  color: valueSxColor,
                  mt: 0.5,
                }}
              >
                {!enabled ? (
                  "—"
                ) : loading ? (
                  <Skeleton
                    variant="text"
                    width={140}
                    height={36}
                    animation="pulse"
                  />
                ) : error ? (
                  "—"
                ) : saldo != null ? (
                  brl.format(saldo)
                ) : (
                  "—"
                )}
              </Typography>
            ) : (
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: valueSxColor }}
              >
                {!enabled ? (
                  "—"
                ) : loading ? (
                  <Skeleton
                    variant="text"
                    width={120}
                    height={32}
                    animation="pulse"
                  />
                ) : error ? (
                  "—"
                ) : saldo != null ? (
                  brl.format(saldo)
                ) : (
                  "—"
                )}
              </Typography>
            )}

            {!enabled ? (
              <Typography variant="caption" color="text.secondary">
                Ative a integração com Melhor Envio para consultar o saldo.
              </Typography>
            ) : null}

            {enabled && !loading && error ? (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            ) : null}

            {enabled && !loading && !error && saldo != null && saldo < 5 ? (
              <Typography variant="caption" color="warning.main">
                Saldo baixo — considere recarregar a carteira no Melhor Envio.
              </Typography>
            ) : null}
          </Stack>

          <Tooltip title="Atualizar saldo">
            <span>
              <IconButton
                size="small"
                onClick={() => void load()}
                disabled={loading || !enabled}
                aria-label="Atualizar saldo Melhor Envio"
                sx={{ border: `1px solid ${borderSoft}` }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    animation: loading
                      ? "me-saldo-spin 0.8s linear infinite"
                      : "none",
                    "@keyframes me-saldo-spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </MDBox>
      </Card>
    </MDBox>
  );
}
